import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MergeIcon from '@mui/icons-material/Merge';

import { useAlert } from '../context/AlertContext';
import {
  useCreateLegalClientFromSubmission,
  useMergeTabletSubmission,
  useRejectTabletSubmission,
} from '../hooks/reactQuerys/TabletSubmissionQueries';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { ITabletSubmission } from '../Interfaces/ITabletSubmission';

interface Props {
  open: boolean;
  onClose: () => void;
  submission: ITabletSubmission | null;
  participants: ILegalClient[];
}

type ExpiryStatus = 'valid' | 'expiring_soon' | 'expired';
interface ExpiryCheck {
  status: ExpiryStatus;
  days: number;
  formattedDate: string;
}

function checkDocumentExpiry(
  expiry: string | undefined,
  warnDays = 90
): ExpiryCheck | null {
  if (!expiry) return null;
  const exp = new Date(expiry);
  if (Number.isNaN(exp.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  exp.setHours(0, 0, 0, 0);
  const days = Math.floor(
    (exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  const formattedDate = exp.toLocaleDateString('de-DE');
  if (days < 0) return { status: 'expired', days, formattedDate };
  if (days <= warnDays) return { status: 'expiring_soon', days, formattedDate };
  return { status: 'valid', days, formattedDate };
}

interface PersonPayload {
  FirstName?: string;
  LastName?: string;
  BirthDate?: string;
  BirthName?: string;
  BirthPlace?: string;
  Nationality?: string;
  DocumentNumber?: string;
  DocumentExpiry?: string;
}
interface AddressPayload {
  Street?: string;
  HouseNumber?: string;
  Zip?: string;
  City?: string;
  Country?: string;
}
interface ContactPayload {
  Phone?: string;
  Email?: string;
}
interface IdScanPayload {
  Method?: 'nfc' | 'ocr' | 'manual' | 'foto';
  FrontPhotoBase64?: string;
  BackPhotoBase64?: string;
  // Optionales Selfie-Foto vom Mandanten — eingeführt im Tablet-Service-
  // Branch (``f820f3c3 "Foto für kreußlein"``). Das Feld ist hier
  // bewusst typisiert, damit der TSX-Code in BEIDEN Branches kompiliert:
  // ohne Selfie-UI (z. B. ``1019-hbci-underkonten``) bleibt das Feld
  // ungenutzt; mit Selfie-UI (z. B. ``1013-dino-tablet-service``/``testing``)
  // kann es ausgelesen werden.
  SelfiePhotoBase64?: string;
}
interface FullPayload {
  Person?: PersonPayload;
  Address?: AddressPayload;
  Contact?: ContactPayload;
  IdScan?: IdScanPayload;
}

const PERSON_LABEL: Record<keyof PersonPayload, string> = {
  FirstName: 'Vorname',
  LastName: 'Nachname',
  BirthDate: 'Geburtsdatum',
  BirthName: 'Geburtsname',
  BirthPlace: 'Geburtsort',
  Nationality: 'Staatsangehörigkeit',
  DocumentNumber: 'Ausweis-Nr.',
  DocumentExpiry: 'Gültig bis',
};

const ADDRESS_LABEL: Record<keyof AddressPayload, string> = {
  Street: 'Straße',
  HouseNumber: 'Hausnummer',
  Zip: 'PLZ',
  City: 'Ort',
  Country: 'Land',
};

const CONTACT_LABEL: Record<keyof ContactPayload, string> = {
  Phone: 'Telefon',
  Email: 'E-Mail',
};

const METHOD_LABEL: Record<NonNullable<IdScanPayload['Method']>, string> = {
  nfc: 'NFC (eID)',
  ocr: 'OCR aus Foto',
  manual: 'Manuelle Eingabe',
  foto: 'Foto + manuelle Eingabe',
};

function _changed(
  prefill: string | undefined,
  value: string | undefined
): boolean {
  const a = (prefill ?? '').trim();
  const b = (value ?? '').trim();
  return a !== b && b.length > 0;
}

export const TabletSubmissionDetailDialog: React.FC<Props> = ({
  open,
  onClose,
  submission,
  participants,
}) => {
  const { showAlert } = useAlert();
  const { mergeIntoLegalClient, isPendingMergeIntoLegalClient } =
    useMergeTabletSubmission();
  const { createLegalClientFromSubmission, isPendingCreateLegalClient } =
    useCreateLegalClientFromSubmission();
  const { rejectSubmission, isPendingRejectSubmission } =
    useRejectTabletSubmission();

  const [photoZoom, setPhotoZoom] = useState<string | null>(null);
  const [targetIdLegalClient, setTargetIdLegalClient] = useState<number | ''>(
    submission?.LegalClient_idLegalClient ?? ''
  );

  const payload: FullPayload = useMemo(() => {
    if (!submission?.Payload) return {};
    if (typeof submission.Payload === 'object') {
      return submission.Payload as FullPayload;
    }
    try {
      return JSON.parse(String(submission.Payload)) as FullPayload;
    } catch {
      return {};
    }
  }, [submission?.Payload]);

  const prefill: FullPayload = useMemo(() => {
    if (!submission?.PrefillPayload) return {};
    if (typeof submission.PrefillPayload === 'object') {
      return submission.PrefillPayload as FullPayload;
    }
    try {
      return JSON.parse(String(submission.PrefillPayload)) as FullPayload;
    } catch {
      return {};
    }
  }, [submission?.PrefillPayload]);

  React.useEffect(() => {
    if (open) {
      setTargetIdLegalClient(submission?.LegalClient_idLegalClient ?? '');
    }
  }, [open, submission?.LegalClient_idLegalClient]);

  if (!submission) return null;

  const idScan = payload.IdScan ?? {};
  const methodLabel =
    METHOD_LABEL[idScan.Method ?? 'manual'] ?? 'Manuelle Eingabe';
  const hasFront = !!idScan.FrontPhotoBase64;
  const hasBack = !!idScan.BackPhotoBase64;
  const hasSelfie = !!idScan.SelfiePhotoBase64;
  const isStatusSubmitted = submission.Status === 'submitted';
  const expiryInfo = checkDocumentExpiry(payload.Person?.DocumentExpiry);

  async function handleCreateNew() {
    if (
      !window.confirm(
        'Aus diesen Daten einen neuen Beteiligten anlegen und dem Vorgang hinzufügen?'
      )
    )
      return;
    try {
      const result = (await createLegalClientFromSubmission(
        submission!.idTabletSubmission
      )) as
        | {
            createdLegalClient?: { FirstName?: string; LastName?: string };
            warning?: string | null;
          }
        | undefined;
      const lc = result?.createdLegalClient;
      const name = [lc?.FirstName, lc?.LastName].filter(Boolean).join(' ');
      showAlert({
        text: name
          ? `Neuer Beteiligter "${name}" wurde dem Vorgang hinzugefügt.`
          : 'Neuer Beteiligter wurde dem Vorgang hinzugefügt.',
        severity: 'success',
      });
      if (result?.warning) {
        showAlert({ text: result.warning, severity: 'warning' });
      }
      onClose();
    } catch {
      showAlert({
        text: 'Beteiligten anlegen fehlgeschlagen.',
        severity: 'error',
      });
    }
  }

  async function handleMerge() {
    if (!targetIdLegalClient) {
      showAlert({
        text: 'Bitte einen Beteiligten auswählen.',
        severity: 'warning',
      });
      return;
    }
    const target = participants.find(
      p => p.idLegalClient === targetIdLegalClient
    );
    const targetName =
      [target?.FirstName, target?.LastName].filter(Boolean).join(' ') ||
      `Beteiligter #${targetIdLegalClient}`;
    if (
      !window.confirm(
        `Daten in "${targetName}" übernehmen? Bestehende Werte werden ergänzt bzw. überschrieben.`
      )
    )
      return;
    try {
      const result = (await mergeIntoLegalClient({
        idTabletSubmission: submission!.idTabletSubmission,
        idLegalClient: Number(targetIdLegalClient),
      })) as
        | {
            merge?: {
              merged: boolean;
              fields: string[];
              warning?: string | null;
            };
          }
        | undefined;
      const merge = result?.merge;
      if (merge?.merged && merge.fields.length > 0) {
        showAlert({
          text: `Daten in "${targetName}" übernommen: ${merge.fields.join(', ')}.`,
          severity: 'success',
        });
      } else {
        showAlert({
          text: `Daten in "${targetName}" übernommen.`,
          severity: 'success',
        });
      }
      if (merge?.warning) {
        showAlert({ text: merge.warning, severity: 'warning' });
      }
      onClose();
    } catch {
      showAlert({ text: 'Übernahme fehlgeschlagen.', severity: 'error' });
    }
  }

  async function handleReject() {
    if (!window.confirm('Diese Eingabe wirklich verwerfen?')) return;
    try {
      await rejectSubmission(submission!.idTabletSubmission);
      showAlert({ text: 'Eingabe abgelehnt.', severity: 'info' });
      onClose();
    } catch {
      showAlert({ text: 'Ablehnung fehlgeschlagen.', severity: 'error' });
    }
  }

  function renderFieldRows<T extends Record<string, string>>(
    labelMap: T,
    data: Partial<Record<keyof T, string | undefined>>,
    prefillData: Partial<Record<keyof T, string | undefined>>
  ) {
    return (Object.keys(labelMap) as Array<keyof T>)
      .filter(
        k =>
          (data[k] ?? '').toString().trim() !== '' ||
          (prefillData[k] ?? '').toString().trim() !== ''
      )
      .map(k => {
        const newVal = (data[k] ?? '').toString();
        const oldVal = (prefillData[k] ?? '').toString();
        const isChanged = _changed(oldVal, newVal);
        return (
          <TableRow key={String(k)}>
            <TableCell sx={{ color: 'text.secondary', width: '40%' }}>
              {labelMap[k]}
            </TableCell>
            <TableCell>
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ flexWrap: 'wrap' }}
              >
                <Typography variant='body2'>{newVal || '–'}</Typography>
                {isChanged && (
                  <Tooltip
                    title={
                      oldVal
                        ? `Vorbelegt: "${oldVal}"`
                        : 'Vom Mandanten ergänzt'
                    }
                  >
                    <Chip
                      label={oldVal ? 'geändert' : 'ergänzt'}
                      size='small'
                      color={oldVal ? 'warning' : 'info'}
                    />
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          </TableRow>
        );
      });
  }

  const isMutating =
    isPendingMergeIntoLegalClient ||
    isPendingCreateLegalClient ||
    isPendingRejectSubmission;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
        <DialogTitle>
          Tablet-Eingabe prüfen
          <Typography
            variant='caption'
            display='block'
            color='text.secondary'
            sx={{ mt: 0.5 }}
          >
            Eingang #{submission.idTabletSubmission} · Tablet „
            {submission.TabletName ?? '–'}" · Methode: {methodLabel}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {(hasFront || hasBack) && (
              <Box>
                <Typography variant='subtitle1' gutterBottom>
                  Personalausweis
                </Typography>
                <Stack direction='row' spacing={2}>
                  {hasFront && (
                    <PhotoThumb
                      src={idScan.FrontPhotoBase64!}
                      label='Vorderseite'
                      onZoom={setPhotoZoom}
                    />
                  )}
                  {hasBack && (
                    <PhotoThumb
                      src={idScan.BackPhotoBase64!}
                      label='Rückseite'
                      onZoom={setPhotoZoom}
                    />
                  )}
                </Stack>
              </Box>
            )}

            {hasSelfie && (
              <Box>
                <Typography variant='subtitle1' gutterBottom>
                  Foto der Person
                </Typography>
                <PhotoThumb
                  src={idScan.SelfiePhotoBase64!}
                  label='Selfie'
                  onZoom={setPhotoZoom}
                />
              </Box>
            )}

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Person
              </Typography>
              {expiryInfo && expiryInfo.status !== 'valid' && (
                <Alert
                  severity={
                    expiryInfo.status === 'expired' ? 'error' : 'warning'
                  }
                  sx={{ mb: 1 }}
                >
                  {expiryInfo.status === 'expired'
                    ? `Personalausweis ist seit ${Math.abs(expiryInfo.days)} Tagen abgelaufen (gültig bis ${expiryInfo.formattedDate}). Bitte vor der Beurkundung einen gültigen Ausweis anfordern.`
                    : `Personalausweis läuft in ${expiryInfo.days} Tagen ab (gültig bis ${expiryInfo.formattedDate}).`}
                </Alert>
              )}
              <Table size='small'>
                <TableBody>
                  {renderFieldRows(
                    PERSON_LABEL,
                    (payload.Person ?? {}) as Record<
                      string,
                      string | undefined
                    >,
                    (prefill.Person ?? {}) as Record<string, string | undefined>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Anschrift
              </Typography>
              <Table size='small'>
                <TableBody>
                  {renderFieldRows(
                    ADDRESS_LABEL,
                    (payload.Address ?? {}) as Record<
                      string,
                      string | undefined
                    >,
                    (prefill.Address ?? {}) as Record<
                      string,
                      string | undefined
                    >
                  )}
                </TableBody>
              </Table>
            </Box>

            <Box>
              <Typography variant='subtitle1' gutterBottom>
                Kontakt
              </Typography>
              <Table size='small'>
                <TableBody>
                  {renderFieldRows(
                    CONTACT_LABEL,
                    (payload.Contact ?? {}) as Record<
                      string,
                      string | undefined
                    >,
                    (prefill.Contact ?? {}) as Record<
                      string,
                      string | undefined
                    >
                  )}
                </TableBody>
              </Table>
            </Box>

            {isStatusSubmitted && (
              <>
                <Divider />
                <Box>
                  <Typography variant='subtitle1' gutterBottom>
                    In bestehenden Beteiligten übernehmen
                  </Typography>
                  <FormControl fullWidth size='small'>
                    <InputLabel id='merge-target-label'>Beteiligter</InputLabel>
                    <Select
                      labelId='merge-target-label'
                      label='Beteiligter'
                      value={targetIdLegalClient}
                      onChange={e =>
                        setTargetIdLegalClient(
                          e.target.value === '' ? '' : Number(e.target.value)
                        )
                      }
                    >
                      <MenuItem value=''>
                        <em>– bitte wählen –</em>
                      </MenuItem>
                      {participants.map(p => (
                        <MenuItem key={p.idLegalClient} value={p.idLegalClient}>
                          {[p.FirstName, p.LastName]
                            .filter(Boolean)
                            .join(' ') || `Beteiligter #${p.idLegalClient}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </>
            )}

            {!isStatusSubmitted && (
              <Alert severity='info'>
                Diese Eingabe ist bereits abgeschlossen (Status:{' '}
                {submission.Status}). Daten werden nur zur Ansicht angezeigt.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Schließen</Button>
          {isStatusSubmitted && (
            <>
              <Button
                color='error'
                onClick={handleReject}
                disabled={isMutating}
              >
                Ablehnen
              </Button>
              <Button
                variant='outlined'
                startIcon={<PersonAddIcon />}
                onClick={handleCreateNew}
                disabled={isMutating}
              >
                Als neuen Beteiligten
              </Button>
              <Button
                variant='contained'
                startIcon={<MergeIcon />}
                onClick={handleMerge}
                disabled={isMutating || !targetIdLegalClient}
              >
                Übernehmen
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!photoZoom}
        onClose={() => setPhotoZoom(null)}
        maxWidth='lg'
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
            onClick={() => setPhotoZoom(null)}
          >
            <CloseIcon />
          </IconButton>
          {photoZoom && (
            <img
              src={photoZoom}
              alt='Ausweis'
              style={{ display: 'block', maxWidth: '100%', maxHeight: '85vh' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const PhotoThumb: React.FC<{
  src: string;
  label: string;
  onZoom: (src: string) => void;
}> = ({ src, label, onZoom }) => (
  <Box sx={{ width: 200 }}>
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Box
      sx={{
        position: 'relative',
        cursor: 'zoom-in',
        '&:hover .zoom-hint': { opacity: 1 },
      }}
      onClick={() => onZoom(src)}
    >
      <img
        src={src}
        alt={label}
        style={{
          width: '100%',
          aspectRatio: '8/5',
          objectFit: 'cover',
          borderRadius: 4,
        }}
      />
      <Box
        className='zoom-hint'
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'white',
          borderRadius: 1,
          p: 0.5,
          opacity: 0,
          transition: 'opacity 0.2s',
        }}
      >
        <OpenInFullIcon fontSize='small' />
      </Box>
    </Box>
  </Box>
);
